import React from 'react';
import {
  Box,
  Button,
  Divider,
  Flex,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Skeleton,
  Table,
  TableContainer,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  useClipboard,
  useDisclosure,
  useToast,
} from '@chakra-ui/react';
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  useReactTable,
} from '@tanstack/react-table';
import {
  AiFieldDefinition,
  FieldDefinition,
  FieldDefinitionTypeLabels,
  getFieldLabel,
} from '../../shared/enrichment';
import {useCustomer} from '../../hooks/api/metadata';
import {pluralize} from '../../lib/helpers';
import {ChevronDown, OverflowMenuVertical} from '@carbon/icons-react';
import {useIsKeyplayAdmin} from '../../hooks/api/user';
import {CrmFieldModal} from './CrmFieldModal';
import {useEnabledIntegration} from '../../hooks/api/integrations';
import {DeleteFieldModal} from './DeleteFieldModal';
import {useEnrichedFieldCount} from '../../hooks/api/enrichment';
import {ObjectId} from 'bson';
import {AgentBuilderModal} from '../Agents/AgentBuilder/AgentBuilderModal';
import {StatusBadge} from '../Agents/StatusBadge';
import {assertNever} from '../../shared/util';

export const Fields = () => {
  const customer = useCustomer();
  const fieldDefinitions = [...(customer.fieldDefinitions ?? [])].sort((a, b) =>
    getFieldLabel(a).localeCompare(getFieldLabel(b))
  );
  const isKeyplayAdmin = useIsKeyplayAdmin();
  const integration = useEnabledIntegration();
  const crmFieldModal = useDisclosure();

  return (
    <Flex direction="column" gap="4">
      <Flex justifyContent="space-between">
        <Box>
          <Text fontWeight="500">Fields</Text>
          <Text textColor="kgray.300" fontSize="sm">
            {pluralize(fieldDefinitions.length, 'field', 'fields')}
          </Text>
        </Box>
        {isKeyplayAdmin && (
          <Menu>
            <MenuButton
              as={Button}
              colorScheme="kbuttonblue"
              fontWeight="normal"
              rightIcon={<ChevronDown />}
            >
              Create Field
            </MenuButton>
            <MenuList>
              <MenuItem
                isDisabled={!integration}
                onClick={crmFieldModal.onOpen}
              >
                From CRM Field
              </MenuItem>
            </MenuList>
          </Menu>
        )}
      </Flex>
      <CustomFieldsList fieldDefinitions={fieldDefinitions} />
      <CrmFieldModal
        isOpen={crmFieldModal.isOpen}
        onClose={crmFieldModal.onClose}
      />
    </Flex>
  );
};

const columnHelper = createColumnHelper<FieldDefinition>();

const getEditStep = (fieldDefinition: AiFieldDefinition) => {
  const {fieldType} = fieldDefinition.config;
  switch (fieldType) {
    case 'boolean':
    case 'category':
    case 'rating':
      return 'editAnalystField';
    case 'legacyResearch':
      return 'editLegacyResearchField';
    case 'research':
      return 'editResearchField';
    case 'legacyTeamSize':
      return 'editLegacyTeamSizeField';
    default:
      assertNever(fieldType);
  }
};

const CustomFieldMenu = ({
  fieldDefinition,
}: {
  fieldDefinition: FieldDefinition;
}) => {
  const {onCopy} = useClipboard(fieldDefinition.id.toString());
  const toast = useToast();
  const deleteFieldModal = useDisclosure();
  const aiFieldBuilderModal = useDisclosure();
  const isKeyplayAdmin = useIsKeyplayAdmin();

  const copyFieldId = () => {
    toast({
      title: 'Field ID copied to clipboard',
      duration: 1_000,
    });
    onCopy();
  };

  if (!isKeyplayAdmin) {
    return null;
  }

  return (
    <Menu>
      <MenuButton>
        <OverflowMenuVertical />
      </MenuButton>
      <MenuList>
        {fieldDefinition.type === 'ai' && fieldDefinition.agentId && (
          <MenuItem onClick={aiFieldBuilderModal.onOpen}>
            Open Agent Builder
            <AgentBuilderModal
              agentId={fieldDefinition.agentId}
              initialStep={getEditStep(fieldDefinition)}
              isOpen={aiFieldBuilderModal.isOpen}
              onClose={aiFieldBuilderModal.onClose}
            />
          </MenuItem>
        )}
        {fieldDefinition.type !== 'ai' && (
          <MenuItem onClick={deleteFieldModal.onOpen} textColor="kred.500">
            Delete Field
            <DeleteFieldModal
              fieldDefinition={fieldDefinition}
              isOpen={deleteFieldModal.isOpen}
              onClose={deleteFieldModal.onClose}
            />
          </MenuItem>
        )}
        <Divider />
        <MenuItem onClick={copyFieldId}>Copy Field ID</MenuItem>
      </MenuList>
    </Menu>
  );
};

const EnrichedFieldCount = ({
  fieldDefinitionId,
}: {
  fieldDefinitionId: ObjectId;
}) => {
  const {data, isPending} = useEnrichedFieldCount({
    fieldDefinitionId,
  });

  return isPending ? (
    <Skeleton>00</Skeleton>
  ) : (
    <>{data?.toLocaleString() ?? '-'}</>
  );
};

const columns = [
  columnHelper.accessor((field) => field, {
    id: 'label',
    header: 'Field Name',
    cell: (info) => {
      const field = info.getValue();
      return <Box whiteSpace="normal">{getFieldLabel(field)}</Box>;
    },
    size: 0,
  }),
  columnHelper.accessor((field) => field, {
    header: 'Type',
    cell: (info) => {
      const field = info.getValue();
      return (
        <Box textColor="kgray.300">{FieldDefinitionTypeLabels[field.type]}</Box>
      );
    },
  }),
  columnHelper.accessor('status', {
    header: 'Status',
    cell: (info) => {
      const status = info.getValue();
      return <StatusBadge status={status} />;
    },
  }),
  columnHelper.accessor((field) => field, {
    header: 'Accounts Enriched',
    cell: (info) => {
      const field = info.getValue();
      return (
        <Box textColor="kgray.300" textAlign="center">
          {field.status !== 'published' ? (
            '-'
          ) : (
            <EnrichedFieldCount fieldDefinitionId={field.id} />
          )}
        </Box>
      );
    },
  }),
  columnHelper.accessor('timestamp', {
    header: 'Created',
    size: 100,
    cell: (info) => {
      return (
        <Box textColor="kgray.300">{info.getValue().toLocaleDateString()}</Box>
      );
    },
  }),
  columnHelper.accessor((field) => field, {
    id: 'actions',
    header: '',
    size: 50,
    cell: (info) => {
      return <CustomFieldMenu fieldDefinition={info.getValue()} />;
    },
  }),
];

const CustomFieldsList = ({
  fieldDefinitions,
}: {
  fieldDefinitions: FieldDefinition[];
}) => {
  const table = useReactTable({
    columns,
    data: fieldDefinitions,
    getCoreRowModel: getCoreRowModel(),
    getRowId: (row) => row.id.toString(),
    defaultColumn: {
      minSize: 0,
      size: 150,
    },
  });

  if (!fieldDefinitions.length) {
    return (
      <Flex direction="column" align="center" p={8}>
        <Text textColor="kgray.300">No custom fields defined.</Text>
      </Flex>
    );
  }

  return (
    <TableContainer border="1px" borderColor="kgray.200" borderRadius="md">
      <Table variant="simple">
        <Thead bg="kgray.100">
          {table.getHeaderGroups().map((headerGroup) => (
            <Tr key={headerGroup.id}>
              {headerGroup.headers.map((header) => (
                <Th
                  key={header.id}
                  w={header.getSize() ? `${header.getSize()}px` : 'auto'}
                >
                  {flexRender(
                    header.column.columnDef.header,
                    header.getContext()
                  )}
                </Th>
              ))}
            </Tr>
          ))}
        </Thead>
        <Tbody>
          {table.getRowModel().rows.map((row) => (
            <Tr key={row.id} role="group">
              {row.getVisibleCells().map((cell) => (
                <Td
                  key={cell.id}
                  _groupHover={{bg: 'kgray.100'}}
                  w={
                    cell.getContext().column.getSize()
                      ? `${cell.getContext().column.getSize()}px`
                      : 'auto'
                  }
                >
                  {flexRender(cell.column.columnDef.cell, cell.getContext())}
                </Td>
              ))}
            </Tr>
          ))}
        </Tbody>
      </Table>
    </TableContainer>
  );
};
